import {
    IEditorCanvas,
    IEditorEditorPhoto, IEditorElement, IEditorElementCollection, IEditorFontStyle,
    IEditorImagePosition,
    IEditorPage, IEditorPhotoGroup, IEditorProject,
    IEditorShape, IEditorTextElement, IEditorUtil, IImageType, Imports, IRepeatedImagePatternOptions
} from "./editor.interfaces";
import {fabric} from "fabric";



type IImageFilter = any;
const FABRIC_EVENT_AFTER_RENDER = 'after:render';

/* tslint:disable no-invalid-this */
const globalImports: Imports = typeof global !== 'undefined' ? global as any : window as any;


export class EditorHelpers {

    private static webpSupport: boolean | null = null;

    public static clamp(low: number, value: number, high: number): number {
        return Math.max(low, Math.min(value, high));
    }

    public static  zoomToFit(innerWidth: number, innerHeight: number, outerWidth: number, outerHeight: number): number {
        return innerWidth / innerHeight > outerWidth / outerHeight ? outerWidth / innerWidth : outerHeight / innerHeight;
    }

    public static getImageSizeFromMax(originalWidth: number, originalHeight: number, maxSize: number, outerWidth?: number, outerHeight?: number): { width: number, height: number } {
        let newWidth;
        let newHeight;
        if (outerWidth && outerHeight) {
            const zoomToFit = originalWidth / originalHeight > outerWidth / outerHeight ? outerWidth / originalWidth : outerHeight / originalHeight;
            newWidth = originalWidth * zoomToFit;
            newHeight = originalHeight * zoomToFit;
        } else {
            if (originalWidth > originalHeight) {
                newWidth = maxSize;
                newHeight = maxSize * (originalHeight / originalWidth);
            } else {
                newHeight = maxSize;
                newWidth = maxSize * (originalWidth / originalHeight);
            }
        }
        return {width: newWidth, height: newHeight};

    }

    public static  isElementAdded(photoObject: IEditorEditorPhoto): boolean {
        return photoObject._element && photoObject._element.nodeName === 'CANVAS';
    }

    public static supportsWebp(): boolean {
        if (this.webpSupport !== null) {
            return this.webpSupport;
        }

        try {
            const canvas = document.createElement('canvas');
            canvas.width = 10;
            canvas.height = 10;
            this.webpSupport = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
        } catch (e) {
            this.webpSupport = false;
        }

        return this.webpSupport;
    }

    public static  getPathName (urlString: string): string {
        try {
            const url = new URL(urlString);
            return url.pathname;
        } catch (e) {
            return urlString;
        }
    }

    public static  rotatePoint(x: number, y: number, rotation: number): number[] {
        if (rotation) {
            const radians = fabric.util.degreesToRadians(rotation);
            const sin = Math.sin(radians);
            const cos = Math.cos(radians);

            return [x * cos - y * sin, x * sin + y * cos];
        }

        return [x, y];
    }

    // noinspection JSUnusedGlobalSymbols
    public static  blendOverlay(srcImage: string, overlay: { url?: string, blend?: GlobalCompositeOperation }):Promise<string> {
        return new Promise(async (resolve:(dataUrl:string) => void, reject:(err:string) => void):Promise<void> => {
            Promise.all([EditorHelpers.getImageFromUrl(srcImage), EditorHelpers.getImageFromUrl(overlay.url!)]).then((images:HTMLImageElement[]):void => {
                const c = document.createElement('canvas');
                c.width = images[0].width;
                c.height = images[0].height;
                if (c) {
                    const ctx = c.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(images[0], 0, 0);
                        ctx.globalCompositeOperation = overlay.blend || 'source-over';
                        ctx.drawImage(images[1], 0, 0, c.width, c.height);
                    }
                    resolve(c.toDataURL().split(',', 2)[1]);
                } else {
                    reject('Unable to create canvas element');
                }
            }, err => {
                reject(err);
            });
        });
    }

    public static  revertCoverPageBleed(page: IEditorPage): void {
        if ((page.type === SF.Model.PageTypes.FRONT_COVER || page.type === SF.Model.PageTypes.BACK_COVER || page.type === SF.Model.PageTypes.SPINE) && page.bleed) {
            page.height -=  ((page.bleed[SF.Model.CoverPhotoMarginAttribute.COVER_PHOTO_MARGIN_BOTTOM] || 0) +  (page.bleed[SF.Model.CoverPhotoMarginAttribute.COVER_PHOTO_MARGIN_TOP] || 0));
            if (page.type === SF.Model.PageTypes.FRONT_COVER) {
                page.width -= page.bleed[SF.Model.CoverPhotoMarginAttribute.COVER_PHOTO_MARGIN_RIGHT] || 0;
            } else if (page.type === SF.Model.PageTypes.BACK_COVER) {
                page.width -= page.bleed[SF.Model.CoverPhotoMarginAttribute.COVER_PHOTO_MARGIN_LEFT] || 0;
            }
        }
    }

    public static  boundingBox(angle: number, width: number, height: number, left: number, top: number, marginLeft: number, marginTop: number, marginRight: number, marginBottom: number): number[] {
        left = left || 0;
        top = top || 0;
        marginLeft = marginLeft || 0;
        marginTop = marginTop || 0;
        marginRight = marginRight || 0;
        marginBottom = marginBottom || 0;

        const innerX = -(width / 2) - left;
        const innerY = -(height / 2) - top;

        // calculate angle (counter rotate inner window)
        const t = fabric.util.degreesToRadians(-angle);
        const cos = Math.cos(t);
        const sin = Math.sin(t);

        // calculate each of the points of the inner window
        const sInnerY = -sin * (innerY + marginTop);
        const cInnerY = cos * (innerY + marginTop);
        const sOuterY = -sin * (innerY + height - marginBottom);
        const cOuterY = cos * (innerY + height - marginBottom);

        const cInnerX = cos * (innerX + marginLeft);
        const sInnerX = sin * (innerX + marginLeft);
        const cOuterX = cos * (innerX + width - marginRight);
        const sOuterX = sin * (innerX + width - marginRight);

        const x = [sInnerY + cInnerX, sInnerY + cOuterX, sOuterY + cInnerX, sOuterY + cOuterX];
        const y = [cInnerY + sInnerX, cInnerY + sOuterX, cOuterY + sInnerX, cOuterY + sOuterX];

        return [
            SF.Util.Math.round(Math.min.apply(Math, x), -2),
            SF.Util.Math.round(Math.max.apply(Math, x), -2),
            SF.Util.Math.round(Math.min.apply(Math, y), -2),
            SF.Util.Math.round(Math.max.apply(Math, y), -2)
        ];
    }

    // noinspection JSUnusedGlobalSymbols - used in edits.ts
    public static  rotateImagePosition(imagePosition: IEditorImagePosition, rotation: number): void {
        const widthOffset = (imagePosition.width * imagePosition.zoom) / 2;
        const heightOffset = (imagePosition.height * imagePosition.zoom) / 2;

        const rPosition = EditorHelpers.rotatePoint(
            imagePosition.x + widthOffset,
            imagePosition.y + heightOffset,
            rotation
        );

        // update x and y with difference
        imagePosition.x = rPosition[0] - widthOffset;
        imagePosition.y = rPosition[1] - heightOffset;
    }

    public static  hasFakeSpine(pages: IEditorPage[]): IEditorPage | undefined {
        return pages.find((page: IEditorPage) => {
            return page.type === SF.Model.PageTypes.SPINE && page.width === 0 && page.elements.length >= 0;
        });
    }

    // /////// color detection starts///////
    // public setSelectionBorderColor(lowerCtx: CanvasRenderingContext2D, coords: any, ratio: number): string {
    //     const tlCoords = coords.tl;
    //     const trCoords = coords.tr;
    //     const blCoords = coords.bl;
    //     const brCoords = coords.br;
    //
    //     const topCoords = EditorHelpers.getCoordinates({x: tlCoords.x * ratio - 1, y: tlCoords.y * ratio - 1}, {
    //         x: trCoords.x * ratio + 1,
    //         y: trCoords.y * ratio - 1
    //     });
    //     const bottomCoords = EditorHelpers.getCoordinates({x: blCoords.x * ratio - 1, y: blCoords.y * ratio + 1}, {
    //         x: brCoords.x * ratio + 1,
    //         y: brCoords.y * ratio + 1
    //     });
    //     const leftCoords = EditorHelpers.getCoordinates({x: tlCoords.x * ratio - 1, y: tlCoords.y * ratio - 1}, {
    //         x: blCoords.x * ratio - 1,
    //         y: blCoords.y * ratio + 1
    //     });
    //     const rightCoords = EditorHelpers.getCoordinates({x: trCoords.x * ratio + 1, y: trCoords.y * ratio - 1}, {
    //         x: brCoords.x * ratio + 1,
    //         y: brCoords.y * ratio + 1
    //     });
    //
    //     const avgColorTop = EditorHelpers.getAvgRGBFromCoords(topCoords, lowerCtx);
    //     const avgColorBottom = EditorHelpers.getAvgRGBFromCoords(bottomCoords, lowerCtx);
    //     const avgColorLeft = EditorHelpers.getAvgRGBFromCoords(leftCoords, lowerCtx);
    //     const avgColorRight = EditorHelpers.getAvgRGBFromCoords(rightCoords, lowerCtx);
    //     let transparentSides = (avgColorTop.a == 0 ? 1 : 0) + (avgColorBottom.a == 0 ? 1 : 0) + (avgColorLeft.a == 0 ? 1 : 0) + (avgColorRight.a == 0 ? 1 : 0);
    //     let count = 4 - transparentSides;
    //     let color: string;
    //     const brightnessValue = 123;
    //     if (transparentSides < 4) {
    //         const avgColorR = Math.sqrt(((avgColorTop.a == 0 ? 0 : Math.pow(avgColorTop.r, 2))
    //             + (avgColorBottom.a == 0 ? 0 : Math.pow(avgColorBottom.r, 2))
    //             + (avgColorLeft.a == 0 ? 0 : Math.pow(avgColorLeft.r, 2))
    //             + (avgColorRight.a == 0 ? 0 : Math.pow(avgColorRight.r, 2))) / count);
    //         const avgColorG = Math.sqrt(((avgColorTop.a == 0 ? 0 : Math.pow(avgColorTop.g, 2))
    //             + (avgColorBottom.a == 0 ? 0 : Math.pow(avgColorBottom.g, 2))
    //             + (avgColorLeft.a == 0 ? 0 : Math.pow(avgColorLeft.g, 2))
    //             + (avgColorRight.a == 0 ? 0 : Math.pow(avgColorRight.g, 2))) / count);
    //         const avgColorB = Math.sqrt(((avgColorTop.a == 0 ? 0 : Math.pow(avgColorTop.b, 2))
    //             + (avgColorBottom.a == 0 ? 0 : Math.pow(avgColorBottom.b, 2))
    //             + (avgColorLeft.a == 0 ? 0 : Math.pow(avgColorLeft.b, 2))
    //             + (avgColorRight.a == 0 ? 0 : Math.pow(avgColorRight.b, 2))) / count);
    //
    //         if (EditorHelpers.isBrighterThan(avgColorR, avgColorG, avgColorB, brightnessValue)) {
    //             color = '#000000';
    //         } else {
    //             color = '#FFFFFF';
    //         }
    //
    //     } else {
    //         color = '#000000';
    //     }
    //     return color;
    // }

    public static  calcBrightness(r: number, g: number, b: number): number {
        return (r * 299 + g * 587 + b * 114) / 1000;
    }

    public static  isBrighterThan(r: number, g: number, b: number, brightnessValue: number): boolean {
        return (EditorHelpers.calcBrightness(r, g, b) > brightnessValue);
    }

    public static  getAvgRGBFromCoords(coords: { x: number, y: number }[], ctx: CanvasRenderingContext2D): { r: number, g: number, b: number, a: number } {
        let r = 0;
        let g = 0;
        let b = 0;
        let a = 0;
        let imgData;
        for (let i = 0; i < coords.length; i++) {
            const px = Math.round(coords[i].x);
            // noinspection JSSuspiciousNameCombination
            const py = Math.round(coords[i].y);
            imgData = ctx.getImageData(px, py, 1, 1).data;
            r += Math.pow(imgData[0], 2);
            g += Math.pow(imgData[1], 2);
            b += Math.pow(imgData[2], 2);
            a += Math.pow(imgData[3], 2);
        }
        const avgR = Math.floor(Math.sqrt(r / coords.length));
        const avgG = Math.floor(Math.sqrt(g / coords.length));
        const avgB = Math.floor(Math.sqrt(b / coords.length));
        const avgA = Math.floor(Math.sqrt(a / coords.length));
        return {r: avgR, g: avgG, b: avgB, a: avgA};

    }

    public static  getImageEditsZoomFromZoomFactor(zoomFactor: number, zoomMax: number, zoomMin: number): number {
        const minMaxDifference = zoomMax - zoomMin;
        const zoomIncrement = minMaxDifference / 100;
        const easePercent = (zoomFactor - zoomMin) / zoomIncrement;
        return Math.sqrt(1 - Math.pow(1 - easePercent / 100, 2)) * 100;
    }

    public static  hasBackgroundObject(canvas: IEditorCanvas): boolean {
        const backgroundObject = canvas.getObjects().find((object: fabric.Object) => {
            return object.type == 'background';
        });
        return backgroundObject !== undefined;
    }

    public static  isNonMovableBackground(object: IEditorShape): boolean {
        return object.type == 'background' && object.photoObject.image && (object.photoObject.image as any).type == 'background' && (object as any).locked;
    }

    public static  slope(a: { x: number, y: number }, b: { x: number, y: number }): number | null {
        if (Math.round(a.x * 100) / 100 == Math.round(b.x * 100) / 100) {
            return null;
        }

        return (b.y - a.y) / (b.x - a.x);
    }

    public static intercept(point: { x: number, y: number }, interceptSlope: null | number): number {
        if (interceptSlope === null) {
            // vertical line
            return point.x;
        }

        return point.y - interceptSlope * point.x;
    }

    public static  getCoordinates(start: { x: number, y: number }, end: { x: number, y: number }): { x: number, y: number }[] {
        const m = EditorHelpers.slope(start, end) as number;
        const b = EditorHelpers.intercept(start, m);
        const blockSize = 10;

        const coordinates: { x: number, y: number }[] = [];
        if (Math.round(start.x * 100) / 100 == Math.round(end.x * 100) / 100) {
            if (start.y <= end.y) {
                for (let y = start.y; y <= end.y; y += blockSize) {
                    coordinates.push({x: start.x, y: y});
                }
            } else {
                for (let y = start.y; y >= end.y; y -= blockSize) {
                    coordinates.push({x: start.x, y: y});
                }
            }
        } else {
            if (start.x < end.x) {
                for (let x = start.x; x <= end.x; x += blockSize) {
                    const y = m * x + b;
                    coordinates.push({x: x, y: y});
                }
            } else {
                for (let x = start.x; x >= end.x; x -= blockSize) {
                    const y = m * x + b;
                    coordinates.push({x: x, y: y});
                }
            }
        }
        return coordinates;
    }

    public static  getAverageRGBColor(data: Uint8ClampedArray, blockSize: number): { r: number, g: number, b: number } {
        const length = data.length;
        let i = 0;
        let count = 0;
        const rgb = {r: 0, g: 0, b: 0};

        while ((i += blockSize * 4) < length) {
            ++count;
            rgb.r += Math.pow(data[i], 2);
            rgb.g += Math.pow(data[i + 1], 2);
            rgb.b += Math.pow(data[i + 2], 2);
        }

        rgb.r = Math.sqrt(rgb.r / count);
        rgb.g = Math.sqrt(rgb.g / count);
        rgb.b = Math.sqrt(rgb.b / count);

        return rgb;
    }

    private static  getImageFromUrl(url:string):Promise<HTMLImageElement> {
        return new Promise(async (resolve:(image:HTMLImageElement) => void, reject:(err:string) => void):Promise<void> => {
            const image = document.createElement('img') as HTMLImageElement;
            image.crossOrigin = 'anonymous';
            image.addEventListener('load', () => {
                resolve(image);
            });
            image.addEventListener('error', ev => {
                reject(ev.error);
            });
            const prefix = url.substring(0,4);
            if (prefix !== 'http' && prefix !== 'data') {
                url = 'data:image/png;base64,' + url;
            }
            image.crossOrigin = 'anonymous';
            image.src = url;
        });
    }

    public static setupFilters(currentZone: IEditorShape, canvasZoom: number,
                               filters:IImageFilter[], callback: (index: number,  dataUrl: string) => void):void {

        const SINGLE_IMAGE_FILTER_PREVIEW_SCALE = .25;

        const canvasEl = fabric.util.createCanvasElement();
        const canvas = new SF.Editor.Canvas(canvasEl, {renderOnAddRemove: false});

        const canvasWidth = currentZone.width as number * canvasZoom;
        const canvasHeight = currentZone.height as number * canvasZoom;
        canvas.setDimensions({
            width: Math.floor(canvasWidth),
            height: Math.floor(canvasHeight)
        });
        canvas.setZoom(canvasZoom);

        const group = (currentZone as any).group;

        if (group) {
            group._ungroupifyObject(currentZone);
        }
        const copiedObject: IEditorShape = SF.Util.Translator.cloneShape(currentZone);

        if (group) {
            group._groupifyObject(currentZone);
        }

        copiedObject.effects.filter = '';
        copiedObject.scaleX = SINGLE_IMAGE_FILTER_PREVIEW_SCALE;
        copiedObject.scaleY = SINGLE_IMAGE_FILTER_PREVIEW_SCALE;
        copiedObject.top = 0;
        copiedObject.left = 0;
        canvas.add(copiedObject);
        let waited = false;
        const filterCanvasRenderer = () => {
            if (canvas.objectsToLoad == 0 || waited) {
                canvas.off(FABRIC_EVENT_AFTER_RENDER, filterCanvasRenderer);
                void EditorHelpers.getFilterPreviews(filters, canvas, copiedObject, callback);
            } else {
                waited = true;
                setTimeout(filterCanvasRenderer, 1000);
            }
        };
        canvas.on(FABRIC_EVENT_AFTER_RENDER, filterCanvasRenderer);
        (canvas as any).calcViewportBoundaries();
        canvas.requestRenderAll();
    }

    private static async getFilterPreviews(filters: IImageFilter[], canvas: IEditorCanvas, image: IEditorShape,
                                           callback: (index:number, url:string) => void):Promise<void> {

        if ((image as IEditorPhotoGroup).removeEdge) {
            (image as IEditorPhotoGroup).removeEdge();
        }

        for (let i = 0; i < filters.length; i++) {
            image.effects.filter = filters[i].filter;
            image.applyEffects();
            await EditorHelpers.renderFilterPreview(canvas,  i, image, callback);
        }
    }

    private static renderFilterPreview(canvas: IEditorCanvas, index:number, image: IEditorShape,
                                       callback: (index:number, url:string) => void):Promise<void> {
        return new Promise<void>((resolveExecutor: () => void) => {

            const filterCanvasRenderer = ():void  => {
                if (canvas && canvas.objectsToLoad == 0) {
                    canvas.off(FABRIC_EVENT_AFTER_RENDER, filterCanvasRenderer);
                    callback(index, image.toDataURL({format:'jpeg'}));
                    resolveExecutor();
                }
            };

            canvas.on(FABRIC_EVENT_AFTER_RENDER, filterCanvasRenderer);
            canvas.requestRenderAll();
        });
    }

    public static resetEditsPatternImages(edits: IEditorElementCollection): void {
        // reset pattern image snapshot in edits object as they need to be re-rendered
        edits.elements.forEach((element): void => {
            if (element.pattern?.patternImage) {
                element.pattern.patternImage = undefined;
            }
        });

        edits.pages.forEach((page: IEditorPage): void => {
            page.elements.forEach((element): void => {
                if (element.pattern?.patternImage) {
                    element.pattern.patternImage = undefined;
                }
            });
        });

        edits.visiblePages.forEach((page: IEditorPage): void => {
            page.elements.forEach((element): void => {
                if (element.pattern?.patternImage) {
                    element.pattern.patternImage = undefined;
                }
            });
        });
    }

    public static drawRepeatedImagePattern(
        ctx: CanvasRenderingContext2D,
        opts: IRepeatedImagePatternOptions,
        canvasMargins: { left: number; top: number; right: number; bottom: number },
        zoom: number,
        retinaScaling: number
    ): void {

        if (opts.patternImage?.src && opts.tileWidth !== undefined && opts.tileHeight !== undefined) {

            ctx.save();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.setTransform(1, 0, 0, 1, canvasMargins.left * retinaScaling, canvasMargins.top * retinaScaling);

            const scale = zoom * retinaScaling;

            const gapX = Math.round(opts.gapX * scale);
            const gapY = Math.round(opts.gapY * scale);

            const anchorX = Math.round(opts.anchorX * scale);
            const anchorY = Math.round(opts.anchorY * scale);

            const brickOffset = Math.round(opts.brickOffset * scale);

            const tileWidth = Math.round(opts.tileWidth * retinaScaling);
            const tileHeight = Math.round(opts.tileHeight * retinaScaling);

            const stepX = tileWidth + gapX;
            const stepY = tileHeight + gapY;


            // mirror/flip options (defaults off)
            const mirrorX = opts.mirrorX ?? false; // horizontal mirror
            const flipY = opts.flipY ?? false;     // vertical flip

            // Safety: avoid infinite loops
            if (stepX <= 0 || stepY <= 0) {
                ctx.restore();
                return;
            }

            const marginLeft = Math.round(canvasMargins.left * retinaScaling);
            const marginTop = Math.round(canvasMargins.top * retinaScaling);
            const marginRight = Math.round(canvasMargins.right * retinaScaling);
            const marginBottom = Math.round(canvasMargins.bottom * retinaScaling);

            const canvasW = ctx.canvas.width - marginLeft - marginRight;
            const canvasH = ctx.canvas.height - marginTop - marginBottom;


            // We tile in a coordinate system where the pattern is rotated around the anchor.
            // Strategy:
            // 1) Compute bounds in "unrotated pattern space" that cover the canvas.
            // 2) Loop rows/cols in that space, compute brick shifts, then rotate each tile point
            //    into canvas space and draw.


            const getXY = (x: number, y: number): { x: number; y: number } => {
                const dx = x - anchorX;
                const dy = y - anchorY;
                return {
                    x: dx + anchorX,
                    y: dy + anchorY,
                };
            };

            // Find bounding box of the canvas in pattern space.
            const p0 = getXY(0, 0);
            const p1 = getXY(canvasW, 0);
            const p2 = getXY(0, canvasH);
            const p3 = getXY(canvasW, canvasH);

            const minX = Math.min(p0.x, p1.x, p2.x, p3.x) - tileWidth;
            const maxX = Math.max(p0.x, p1.x, p2.x, p3.x) + tileWidth;
            const minY = Math.min(p0.y, p1.y, p2.y, p3.y) - tileHeight;
            const maxY = Math.max(p0.y, p1.y, p2.y, p3.y) + tileHeight;

            // Compute row/col ranges relative to the anchor tile (row=0,col=0 is anchor).
            const colMin = Math.floor((minX - anchorX) / stepX);
            const colMax = Math.ceil((maxX - anchorX) / stepX);
            const rowMin = Math.floor((minY - anchorY) / stepY);
            const rowMax = Math.ceil((maxY - anchorY) / stepY);

            const pad = (gapX === 0 && gapY === 0) ? 1 : 0;


            // Draw tiles.
            for (let row = rowMin; row <= rowMax; row++) {
                // Classic brick: shift every odd row by half a step in X.
                const rowBrickShift =
                    opts.brickOffsetMode === 'row' && (row & 1) === 1 ? brickOffset : 0;

                for (let col = colMin; col <= colMax; col++) {

                    // Brick-by-column: shift every odd column by half a step in Y.
                    const colBrickShift =
                        opts.brickOffsetMode === 'column' && (col & 1) === 1 ? brickOffset : 0;

                    const px = anchorX + col * stepX + rowBrickShift;
                    const py = anchorY + row * stepY + colBrickShift;

                    const { x, y } = getXY(px, py);


                    // Optional fast culling in canvas space.
                    if (
                        x + tileWidth + pad < 0 ||
                        y + tileHeight + pad < 0 ||
                        x - pad > canvasW ||
                        y - pad > canvasH
                    ) {
                        continue;
                    }

                    // decide per-tile mirror/flip anchored around the original tile
                    // - anchor tile (row=0,col=0) stays unchanged
                    const doMirror = mirrorX && !(row === 0 && col === 0) && (col & 1) === 1;
                    const doFlip = flipY && !(row === 0 && col === 0) && (row & 1) === 1;

                    // draw normally OR via a local transform
                    if (!doMirror && !doFlip) {

                        const dx = Math.round(x) - pad;
                        const dy = Math.round(y) - pad;
                        ctx.drawImage(opts.patternImage, dx, dy, tileWidth + pad * 2, tileHeight + pad * 2);

                    } else {
                        ctx.save();

                        const dx = Math.round(x) - pad;
                        const dy = Math.round(y) - pad;
                        ctx.translate(dx, dy);

                        // mirror/flip around the *expanded* box
                        const dw = tileWidth + pad * 2;
                        const dh = tileHeight + pad * 2;

                        // mirror around the tile's own top-left so it still occupies same box
                        if (doMirror) {
                            ctx.translate(dw, 0);
                            ctx.scale(-1, 1);
                        }

                        // flip around the tile's own top-left so it still occupies same box
                        if (doFlip) {
                            ctx.translate(0, dh);
                            ctx.scale(1, -1);
                        }

                        ctx.drawImage(opts.patternImage, 0, 0, dw, dh);
                        ctx.restore();

                    }
                }
            }
            ctx.restore();
        }
    }

    public static loadPatternImages(fabricCanvas: IEditorCanvas): Promise<void> {
        return new Promise<void>((resolve) => {
            const objectsWithPattern = fabricCanvas.getObjects().filter((object: fabric.Object) => {
                return this.hasUnloadedPatternImages(object as IEditorShape);
            }) as IEditorShape[];
            let pendingPatternImageLoads = objectsWithPattern.length;

            if (pendingPatternImageLoads === 0) {
                resolve();
                return;
            }

            const tryResolve = () => {
                pendingPatternImageLoads--;
                if (pendingPatternImageLoads === 0) {
                    fabricCanvas.renderAll();
                    resolve();
                }
            };

            objectsWithPattern.forEach((object: IEditorShape) => {
                const img = new Image();
                img.onload = () => {
                    if (object.pattern) {
                        const boundingRect = object.getBoundingRect(true, true);
                        object.pattern.tileHeight = img.height;
                        object.pattern.tileWidth = img.width;
                        object.pattern.patternImage = img;
                        object.pattern.anchorX = boundingRect.left;
                        object.pattern.anchorY = boundingRect.top;
                    }
                    tryResolve();
                };
                img.onerror = () => {
                    tryResolve();
                };
                // detect image format from photoObject image and create toDataURL accordingly
                const imageType = this.getImageType(object.photoObject.image.name);

                img.src = object.toDataURL({multiplier: fabricCanvas.getZoom(), format: imageType, quality: 1});
            });
        });

    }

    public static hasUnloadedPatternImages(object: IEditorShape): boolean {
        return object.pattern !== undefined && !object.pattern.patternImage?.src;
    }

    public static hasPatternObject(canvas: IEditorCanvas) {
        return canvas.getObjects().some((object) => {
            return !!(object as IEditorShape).pattern;
        });
    }

    public static getImageType(filename: string): IImageType {
        const match = filename.toLowerCase().match(/\.(webp|png|jpe?g)$/);
        if (!match) {
            return 'unknown';
        }
        return match[1] === 'jpg' ? 'jpeg' : match[1] as IImageType;
    }

    /////////color detection ends////////
}

export class EditorFonts  {

    public fromProject(project: IEditorProject): { [key: string]: string[] } {
        const fontDescriptions: { [key: string]: string[] } = {};

        project.pages.forEach((page: IEditorPage): void => {
            this.fromPage(page, fontDescriptions);
        });

        return fontDescriptions;
    }

    public fromPage(page: IEditorPage, fontDescriptions?: { [key: string]: string[] }): { [key: string]: string[] } {
        fontDescriptions = fontDescriptions || {};

        page.elements
            .filter((element: IEditorElement): boolean => {
                return element instanceof SF.Model.TextElement;
            })
            .forEach((textElement: IEditorElement): void => {
                this.fromElement((textElement as IEditorTextElement), fontDescriptions);
            });

        return fontDescriptions;
    }

    public fromElement(element: IEditorTextElement, descriptions?: { [key: string]: string[] }): { [key: string]: string[] } {
        const fontDescriptions = descriptions || {};

        // find font used in rootStyle
        this.checkAndAdd(element.rootStyle, fontDescriptions);

        // find all fonts used in styles
        if (element.styles) {
            Object.keys(element.styles).forEach((style: string): void => {
                if (element.styles[style]) {
                    Object.keys(element.styles[style]).forEach((char: any): void => {
                        this.checkAndAdd(char, fontDescriptions);
                    });
                }
            });
        }

        return fontDescriptions;
    }

    public fromShape(shape: IEditorShape, descriptions?: { [key: string]: string[] }): { [key: string]: string[] } {
        const fontDescriptions = descriptions || {};

        this.checkAndAdd(shape, fontDescriptions);

        if (shape.styles) {
            Object.keys(shape.styles).forEach((style: string): void => {
                if (shape.styles[style]) {
                    Object.keys(shape.styles[style]).forEach((font: any): void => {
                        this.checkAndAdd(font, fontDescriptions);
                    });
                }
            });
        }

        return fontDescriptions;
    }

    public checkAndAdd(font: any, fontDescriptions: { [key: string]: string[] }): { [key: string]: string[] } {
        fontDescriptions = fontDescriptions || {};
        const fontFamily = font.fontFamily;

        // if a font family declaration exists
        if (fontFamily) {
            // add it to list if it doesn't exist
            if (!fontDescriptions.hasOwnProperty(fontFamily)) {
                fontDescriptions[fontFamily] = [];
            }

            // get style
            const style = this.getStyleFromStylesObject(font);

            // add it as one of the styles if it doesn't already exist
            if (fontDescriptions[fontFamily].indexOf(style) === -1) {
                fontDescriptions[fontFamily].push(style);
            }
        }

        return fontDescriptions;
    }

    public getStyleFromProperties(isBold: boolean, isItalic: boolean): string {
        if (isBold && isItalic) {
            return 'bi';
        }

        if (isBold) {
            return 'b';
        }

        return isItalic ? 'i' : 'r';
    }

    public getStyleFromStylesObject(font: IEditorFontStyle): string {
        return this.getStyleFromProperties(font.fontWeight === 'bold', font.fontStyle === 'italic');
    }
}

function eventHandler(callback: Function): () => void {
    return function (): void {
        // remove first argument
        Array.prototype.shift.apply(arguments);

        // apply callback with just what we care about
        callback.apply(this, arguments);
    };
}

export class EditorMath  {
    /**
     * Decimal adjustment of a number.
     *
     * @param {String}  type  The type of adjustment.
     * @param {Number}  value The number.
     * @param {Number} exp   The exponent (the 10 logarithm of the adjustment base).
     * @returns {Number} The adjusted value.
     */
    private decimalAdjust(type: string, value: number, exp?: number|string): number {
        // If the exp is undefined or zero...
        if (typeof exp === 'undefined' || +exp === 0) {
            return (Math as any)[type](value);
        }
        value = +value;
        exp = +exp;
        // If the value is not a number or the exp is not an integer...
        // noinspection SuspiciousTypeOfGuard
        if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
            return NaN;
        }
        // Shift
        let arrValue = value.toString().split('e');
        value = (Math as any)[type](+(arrValue[0] + 'e' + (arrValue[1] ? (+arrValue[1] - exp) : -exp)));
        // Shift back
        arrValue = value.toString().split('e');
        return +(arrValue[0] + 'e' + (arrValue[1] ? (+arrValue[1] + exp) : exp));
    }

    public round(value: number, exp?: number): number {
        return this.decimalAdjust('round', value, exp);
    }

    public floor(value: number, exp?: number): number {
        return this.decimalAdjust('floor', value, exp);
    }

    public ceil(value: number, exp?: number): number {
        return this.decimalAdjust('ceil', value, exp);
    }

}

const SF = globalImports.SF || (globalImports.SF = {});
SF.Util = SF.Util || {} as IEditorUtil;

SF.Util.Fonts = new EditorFonts();
SF.Util.EventHandler = eventHandler;
SF.Util.Math = new EditorMath();
SF.Util.Helpers = EditorHelpers;

